/**
 * La chat: la home del prodotto.
 *
 * Modello d'interazione delle app AI frontier — streaming, cronologia, passi
 * di lavoro visibili — con la presenza di un'app di messaggistica: testata con
 * il contesto, avatar, bolle, orari, spunte. Una chat anonima sembra un
 * terminale; questa deve sembrare un collega.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowUp,
  AtSign,
  Check,
  CheckCheck,
  FileText,
  ImageIcon,
  Loader2,
  Menu,
  Mic,
  Paperclip,
  Sparkles,
  Square,
  X,
  SquarePen,
} from "lucide-react";
import {
  ALLEGATI_PER_MESSAGGIO,
  BYTE_MAX_ALLEGATO,
  CARATTERI_MAX_MESSAGGIO,
  BYTE_MAX_IMMAGINE,
  TIPI_ALLEGATO,
} from "@otto/shared";
import type {
  AgentStep,
  ChatBlock,
  ChatEvent,
  ChatMessageView,
  ListingView,
} from "@otto/shared";
import { ApiError, api } from "../lib/api.ts";
import { Prose } from "../ui/Prose.tsx";
import { messaggioDi } from "../lib/errori.ts";
import { postAndStream } from "../lib/stream.ts";
import { useStore } from "../lib/store.tsx";
import { useSchermoStretto } from "../lib/schermo.ts";
import { BlockRenderer } from "../blocks/index.tsx";
import { Avatar, Card, IconButton, cx } from "../ui/primitives.tsx";
import {
  MentionPopup,
  applyMention,
  findMention,
  type MentionState,
} from "../ui/Mentions.tsx";
import { PendingBanner } from "./PendingBanner.tsx";
import { Campanella } from "../ui/Campanella.tsx";

interface LiveTurn {
  text: string;
  blocks: ChatBlock[];
  steps: AgentStep[];
  streaming: boolean;
}

const EMPTY_TURN: LiveTurn = {
  text: "",
  blocks: [],
  steps: [],
  streaming: false,
};

export function Chat({
  threadId,
  onNuova,
  onConversazioni,
  onThreadCreated,
  onThreadMancante,
}: {
  threadId: string | null;
  /** Una conversazione nuova, dall'intestazione: su telefono non c'è altro posto. */
  onNuova?: () => void;
  /** Le conversazioni di prima, dal tasto con le tre barrette: solo su telefono. */
  onConversazioni?: () => void;
  onThreadCreated: (id: string) => void;
  /** La conversazione non esiste più (o è di un altro account): si chiude. */
  onThreadMancante?: (id: string) => void;
}) {
  const store = useStore();
  const { notify } = store;
  const [messages, setMessages] = useState<ChatMessageView[]>([]);
  const [live, setLive] = useState<LiveTurn>(EMPTY_TURN);
  const [briefing, setBriefing] = useState<ChatBlock | null>(null);
  const [draft, setDraft] = useState("");
  const [allegati, setAllegati] = useState<AllegatoInCorso[]>([]);

  /**
   * Il file si carica appena scelto, mentre l'host scrive: all'invio c'è già
   * l'id, e il messaggio non aspetta l'upload.
   */
  const allega = useCallback((files: FileList | null) => {
    const scelti = [...(files ?? [])];
    setAllegati((prima) => {
      const posto = ALLEGATI_PER_MESSAGGIO - prima.length;
      const nuovi = scelti
        .slice(0, Math.max(0, posto))
        .map((file): AllegatoInCorso => {
          const chiave = `${file.name}_${file.size}_${Math.random()}`;
          const errore = erroreAllegato(file);
          if (!errore) {
            void api
              .caricaAllegato(file)
              .then(({ allegato }) =>
                setAllegati((ora) =>
                  ora.map((a) =>
                    a.chiave === chiave
                      ? { ...a, stato: "pronto", id: allegato.id }
                      : a,
                  ),
                ),
              )
              .catch((e: unknown) =>
                setAllegati((ora) =>
                  ora.map((a) =>
                    a.chiave === chiave
                      ? { ...a, stato: "errore", errore: messaggioDi(e) }
                      : a,
                  ),
                ),
              );
          }
          return {
            chiave,
            nome: file.name,
            tipo: file.type,
            stato: errore ? "errore" : "carico",
            ...(errore ? { errore } : {}),
          };
        });
      return [...prima, ...nuovi];
    });
  }, []);
  const togliAllegato = useCallback(
    (chiave: string) =>
      setAllegati((prima) => prima.filter((a) => a.chiave !== chiave)),
    [],
  );
  const [loading, setLoading] = useState(false);

  const abort = useRef<AbortController | null>(null);
  const bottom = useRef<HTMLDivElement | null>(null);
  const input = useRef<HTMLTextAreaElement | null>(null);

  /**
   * La conversazione che è già davanti all'host.
   *
   * Una conversazione nuova nasce **durante** lo streaming: il primo evento
   * porta il suo id, il genitore lo mette nello stato, e l'effetto qui sotto
   * lo leggeva come «l'host è passato a un'altra conversazione». Azzerava il
   * turno in corso — il testo arrivato fin lì, i passi di lavoro, la card
   * della proposta appena mostrata — e ricaricava dal server un thread che
   * conteneva ancora solo la domanda. A schermo la chat diventava bianca per
   * qualche secondo, e quello che Otto stava facendo tornava solo a fine
   * turno: sembrava che non avesse capito, o che si fosse piantato.
   *
   * Quello che si ha già davanti non si ricarica.
   */
  const aSchermo = useRef<string | null>(null);
  // In un riferimento: è una funzione nuova a ogni render del genitore, e
  // metterla fra le dipendenze ricaricherebbe la conversazione ogni volta.
  const quandoManca = useRef(onThreadMancante);
  quandoManca.current = onThreadMancante;

  useEffect(() => {
    if (threadId && aSchermo.current === threadId) return;

    let cancelled = false;
    setLive(EMPTY_TURN);

    const load = async () => {
      if (!threadId) {
        setMessages([]);
        aSchermo.current = null;
        return;
      }
      setLoading(true);
      try {
        const { messages: loaded } = await api.messages(threadId);
        if (!cancelled) setMessages(loaded);
      } catch (errore) {
        if (cancelled) return;
        // Una conversazione che non c'è più non è un guasto da annunciare: è
        // quasi sempre quella ricordata dal browser, di un account di prima.
        // Si chiude in silenzio e si riparte dalla chat vuota.
        if (errore instanceof ApiError && errore.status === 404) {
          quandoManca.current?.(threadId);
          return;
        }
        // Una conversazione che non si carica non deve restare bianca e muta.
        notify(messaggioDi(errore));
      } finally {
        // Anche dopo un errore: è comunque quella aperta, e riprovare a
        // ricaricarla da sola non aggiungerebbe niente.
        if (!cancelled) {
          setLoading(false);
          aSchermo.current = threadId;
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
    // `notify` è stabile; `store` intero no, e dipendere da lui rifarebbe la
    // richiesta a ogni cambio di stato dell'app.
  }, [threadId, notify]);

  useEffect(() => {
    if (messages.length > 0 || live.text) return;
    void api
      .briefing()
      .then(({ block }) => setBriefing(block))
      .catch(() => setBriefing(null));
  }, [messages.length, live.text, store.actions]);

  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, live]);

  // Un comando preparato altrove finisce nell'input, non nell'invio.
  useEffect(() => {
    if (!store.promptDraft) return;
    setDraft(store.promptDraft);
    store.setPromptDraft(null);
    input.current?.focus();
  }, [store]);

  const send = useCallback(
    async (text: string) => {
      // Un file da solo è un messaggio: si chiede di leggerlo.
      const pronti = allegati.filter((a) => a.stato === "pronto" && a.id);
      const trimmed =
        text.trim() ||
        (pronti.length > 0 ? "Ti allego questo documento: leggilo." : "");
      if (!trimmed || live.streaming) return;
      // Un file ancora in viaggio non si lascia indietro in silenzio.
      if (allegati.some((a) => a.stato === "carico")) return;

      setDraft("");
      setAllegati([]);
      setBriefing(null);
      setMessages((previous) => [
        ...previous,
        {
          id: `local_${Date.now()}`,
          role: "host",
          text: trimmed,
          blocks: pronti.map((a) => ({
            id: `allegato_${a.id}`,
            type: "allegato" as const,
            allegatoId: a.id!,
            nome: a.nome,
            tipo: a.tipo,
          })),
          steps: [],
          at: new Date().toISOString(),
        },
      ]);
      setLive({ ...EMPTY_TURN, streaming: true });

      const controller = new AbortController();
      abort.current = controller;
      let createdThreadId = threadId;

      const onEvent = (event: ChatEvent) => {
        switch (event.type) {
          case "thread":
            if (!createdThreadId) {
              createdThreadId = event.threadId;
              // La conversazione nuova è già questa, con dentro il turno che
              // sta arrivando: segnarla **prima** di dirlo al genitore evita
              // che l'effetto qui sopra la ricarichi e cancelli quello che
              // l'host sta guardando.
              aSchermo.current = event.threadId;
              onThreadCreated(event.threadId);
            }
            break;
          case "text_delta":
            setLive((previous) => ({
              ...previous,
              text: previous.text + event.text,
            }));
            break;
          case "step":
            setLive((previous) => ({
              ...previous,
              steps: upsertStep(previous.steps, event.step),
            }));
            break;
          case "block":
            setLive((previous) => ({
              ...previous,
              blocks: [...previous.blocks, event.block],
            }));
            break;
          case "error":
            setLive((previous) => ({
              ...previous,
              blocks: [
                ...previous.blocks,
                {
                  id: `err_${Date.now()}`,
                  type: "notice",
                  tone: "errore",
                  title: null,
                  message: event.message,
                },
              ],
            }));
            break;
          default:
            break;
        }
      };

      try {
        await postAndStream(
          "/api/chat/messages",
          {
            text: trimmed,
            ...(threadId ? { threadId } : {}),
            ...(pronti.length > 0
              ? { allegati: pronti.map((a) => a.id!) }
              : {}),
          },
          { onEvent, signal: controller.signal },
        );
      } catch (errore) {
        // "Ferma" passa di qui: è l'host che ha interrotto, non un guasto —
        // dirgli che la rete è caduta sarebbe una bugia.
        const interrotto =
          errore instanceof DOMException && errore.name === "AbortError";

        if (!interrotto) {
          // Prima questa `fetch` non era protetta da niente: rete giù o server
          // spento producevano una promise rifiutata e nessuno se ne
          // accorgeva — a schermo restava il puntino che pulsa, per sempre.
          onEvent({
            type: "error",
            message: messaggioDi(
              errore,
              "Non sono riuscito a risponderti: la connessione è caduta a metà. Riprova.",
            ),
          });
        }
      } finally {
        abort.current = null;
        setLive((previous) => ({ ...previous, streaming: false }));
        void store.refreshActions();

        if (createdThreadId) {
          void api
            .messages(createdThreadId)
            .then(({ messages: fresh }) => {
              // L'host può aver cambiato conversazione mentre Otto scriveva:
              // quello che arriva adesso è di un'altra, e non va messo qui.
              if (aSchermo.current !== createdThreadId) return;
              setMessages((local) =>
                fresh.length >= local.length ? fresh : local,
              );
              setLive((previous) =>
                fresh.length >= 2
                  ? EMPTY_TURN
                  : { ...previous, streaming: false },
              );
            })
            .catch(() => undefined);
        }
      }
    },
    [live.streaming, threadId, onThreadCreated, store, allegati],
  );

  const stop = () => {
    abort.current?.abort();
    abort.current = null;
    setLive((previous) => ({ ...previous, streaming: false }));
  };

  const dictation = useDictation((text) => {
    setDraft((previous) => (previous ? `${previous} ${text}` : text));
    input.current?.focus();
  });

  const showEmpty =
    messages.length === 0 && !live.text && !live.streaming && !loading;

  return (
    <div className="flex h-full flex-col bg-surface">
      <ChatHeader onNuova={onNuova} onConversazioni={onConversazioni} />

      <div
        className={cx(
          "scroll-soft flex-1 overflow-y-auto",
          showEmpty && "flex items-center",
        )}
      >
        <div
          className={cx(
            "mx-auto flex w-full max-w-[720px] flex-col gap-4 px-4 pb-6 sm:px-6",
            showEmpty ? "pt-0" : "pt-6",
          )}
        >
          {showEmpty ? <Welcome onSend={send} /> : null}

          {messages.map((message, index) => (
            <Message
              key={message.id}
              message={message}
              onSend={send}
              showAvatar={messages[index - 1]?.role !== message.role}
            />
          ))}

          {live.streaming || live.text || live.blocks.length > 0 ? (
            <LiveMessage turn={live} onSend={send} />
          ) : null}

          <div ref={bottom} />
        </div>
      </div>

      <Composer
        draft={draft}
        setDraft={setDraft}
        allegati={allegati}
        onAllega={allega}
        onTogliAllegato={togliAllegato}
        onSend={send}
        onStop={stop}
        streaming={live.streaming}
        briefing={showEmpty ? briefing : null}
        dictation={dictation}
        inputRef={input}
      />
    </div>
  );
}

// ── Testata ─────────────────────────────────────────────────────────────────

/**
 * La testata dà alla chat un interlocutore e un contesto: chi è, su cosa sta
 * lavorando, com'è messo l'account adesso.
 */
function ChatHeader({
  onNuova,
  onConversazioni,
}: {
  onNuova?: (() => void) | undefined;
  onConversazioni?: (() => void) | undefined;
}) {
  const store = useStore();

  return (
    <header className="flex items-center gap-x-4 border-b border-line px-5 py-3.5 sm:px-6">
      {/* Su telefono la colonna con le conversazioni non c'è: le tre
          barrette aprono il cassetto di quelle vecchie e la penna ne comincia
          una nuova, come in ogni app di chat. Su schermo largo sono di troppo. */}
      {onConversazioni || onNuova ? (
        <span className="-ml-1.5 flex items-center gap-0.5 lg:hidden">
          {onConversazioni ? (
            <IconButton label="Conversazioni" onClick={onConversazioni}>
              <Menu size={19} />
            </IconButton>
          ) : null}
          {onNuova ? (
            <IconButton label="Nuova conversazione" onClick={onNuova}>
              <SquarePen size={17} />
            </IconButton>
          ) : null}
        </span>
      ) : null}
      {/* Niente titolo «Assistente Otto» (14/10/2026): si sa con chi si
          parla. Resta la campanella, e un segnale solo quando serve: se le
          automazioni sono ferme, l'host lo deve vedere da qui. */}
      <div className="ml-auto flex min-w-0 items-center gap-2.5">
        {store.account?.killSwitch ? (
          <span className="rounded-pill bg-danger-soft px-2.5 py-1 text-[12px] font-bold text-danger">
            Automazioni ferme
          </span>
        ) : null}
        <Campanella />
      </div>
    </header>
  );
}

// ── Messaggi ────────────────────────────────────────────────────────────────

function Message({
  message,
  onSend,
  showAvatar,
}: {
  message: ChatMessageView;
  onSend: (text: string) => void;
  showAvatar: boolean;
}) {
  const store = useStore();

  if (message.role === "host") {
    return (
      <div className="animate-rise flex items-end justify-end gap-2.5">
        <div className="flex max-w-[76%] flex-col items-end gap-1">
          {message.blocks.map((b) =>
            b.type === "allegato" ? (
              <FileChip key={b.id} nome={b.nome} tipo={b.tipo} />
            ) : null,
          )}
          <div className="rounded-card-lg rounded-br-md bg-accent px-4 py-2.5 text-[14.5px] leading-relaxed text-ink-inverse shadow-soft">
            {message.text}
          </div>
          <Timestamp at={message.at} read />
        </div>
        {showAvatar ? (
          <Avatar name={store.account?.nome ?? "Host"} size={32} />
        ) : (
          <span className="size-8 shrink-0" />
        )}
      </div>
    );
  }

  return (
    <div className="animate-rise flex items-start gap-2.5">
      {showAvatar ? (
        <div className="grid size-8 shrink-0 place-items-center rounded-full bg-accent text-ink-inverse">
          <Sparkles size={15} />
        </div>
      ) : (
        <span className="size-8 shrink-0" />
      )}

      <div className="flex min-w-0 flex-1 flex-col gap-2.5">
        {message.steps.length > 0 ? <Steps steps={message.steps} /> : null}

        {message.text ? (
          <div className="flex max-w-[86%] flex-col gap-1">
            <div className="rounded-card-lg rounded-bl-md bg-surface-muted px-4 py-2.5 text-[14.5px] leading-relaxed text-ink">
              <Prose text={message.text} />
            </div>
            <Timestamp at={message.at} />
          </div>
        ) : null}

        {message.blocks.map((block) => (
          <BlockRenderer key={block.id} block={block} onSend={onSend} />
        ))}
      </div>
    </div>
  );
}

function LiveMessage({
  turn,
  onSend,
}: {
  turn: LiveTurn;
  onSend: (text: string) => void;
}) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="grid size-8 shrink-0 place-items-center rounded-full bg-accent text-ink-inverse">
        <Sparkles size={15} />
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-2.5">
        {turn.steps.length > 0 ? <Steps steps={turn.steps} /> : null}

        {turn.text ? (
          <div className="max-w-[86%] rounded-card-lg rounded-bl-md bg-surface-muted px-4 py-2.5 text-[14.5px] leading-relaxed text-ink">
            <Prose text={turn.text} cursor={turn.streaming} />
          </div>
        ) : turn.streaming && turn.steps.length === 0 ? (
          <Typing />
        ) : null}

        {turn.blocks.map((block) => (
          <BlockRenderer key={block.id} block={block} onSend={onSend} />
        ))}
      </div>
    </div>
  );
}

function Timestamp({ at, read }: { at: string; read?: boolean }) {
  const time = new Intl.DateTimeFormat("it-IT", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Rome",
  }).format(new Date(at));

  return (
    <span className="flex items-center gap-1 px-1 text-[11px] tabular-nums text-ink-faint">
      {time}
      {read ? (
        <CheckCheck size={12} className="text-accent" />
      ) : (
        <Check size={12} />
      )}
    </span>
  );
}

function Typing() {
  return (
    <div className="flex w-fit items-center gap-1.5 rounded-card-lg rounded-bl-md bg-surface-muted px-4 py-3">
      {[0, 1, 2].map((index) => (
        <span
          key={index}
          className="pulse-dot size-1.5 rounded-full bg-ink-faint"
          style={{ animationDelay: `${index * 180}ms` }}
        />
      ))}
    </div>
  );
}

/** I passi di lavoro: in italiano, da collaboratore, mai gergo. */
function Steps({ steps }: { steps: AgentStep[] }) {
  const running = steps.some((step) => step.status === "in_corso");
  const [open, setOpen] = useState(false);
  const visible = open || running ? steps : steps.slice(-1);

  return (
    <button
      type="button"
      onClick={() => setOpen((value) => !value)}
      className="flex w-fit max-w-full flex-col gap-1 rounded-card px-2 py-1 text-left transition-colors duration-quick hover:bg-surface-muted"
    >
      {visible.map((step) => (
        <span
          key={step.id}
          className="flex items-center gap-2 text-[12.5px] text-ink-faint"
        >
          {step.status === "in_corso" ? (
            <Loader2 size={12} className="animate-spin" />
          ) : step.status === "errore" ? (
            <span className="size-1.5 rounded-full bg-danger" />
          ) : (
            <span className="size-1.5 rounded-full bg-ok/60" />
          )}
          <span className={cx(step.status === "in_corso" && "text-ink-soft")}>
            {step.label}
          </span>
        </span>
      ))}
      {steps.length > 1 && !open && !running ? (
        <span className="pl-5 text-[11px] text-ink-faint/70">
          e altri {steps.length - 1} passaggi
        </span>
      ) : null}
    </button>
  );
}

// ── Schermata iniziale ──────────────────────────────────────────────────────

function Welcome({ onSend }: { onSend: (text: string) => void }) {
  const store = useStore();
  const hour = new Date().getHours();
  const greeting =
    hour < 12 ? "Buongiorno" : hour < 18 ? "Buon pomeriggio" : "Buonasera";

  const [immobili, setImmobili] = useState<ListingView[]>([]);
  useEffect(() => {
    void api
      .listings()
      .then(({ listings }) => setImmobili(listings))
      .catch(() => setImmobili([]));
  }, []);

  /**
   * Gli esempi parlano delle case vere.
   *
   * Prima citavano «il monolocale di Sanremo», che sui dati del simulatore
   * esisteva e sull'account di un cliente no: il primo suggerimento che Otto
   * dà di sé era un immobile inventato, e chi lo tocca si sente rispondere
   * "non trovo nessun monolocale a Sanremo". Con un nome vero l'esempio
   * funziona davvero; senza immobili in cache si resta sul generico.
   */
  const casa = immobili[0]?.nickname;
  const esempi = casa
    ? [
        `Alza di 20 € a notte su "${casa}" nelle prossime due settimane.`,
        "Minimo 3 notti su tutti gli appartamenti nel ponte di Ognissanti.",
        "Chi pulisce domani e a che ora?",
      ]
    : [
        "Alza di 20 € a notte sull'appartamento più pieno, dal 10 al 25.",
        "Minimo 3 notti su tutti gli appartamenti nel ponte di Ognissanti.",
        "Chi pulisce domani e a che ora?",
      ];

  return (
    <div className="flex flex-col items-start gap-4">
      <div>
        <h2 className="text-[22px] font-extrabold tracking-tight text-ink sm:text-[26px]">
          {greeting}.
        </h2>
        <p className="mt-1 max-w-md text-[14px] leading-relaxed text-ink-soft sm:text-[15px]">
          Scrivi cosa vuoi cambiare nei tuoi appartamenti. Ti mostro cosa
          cambierebbe, poi decidi tu.
        </p>
      </div>

      {/* Su telefono gli esempi sono piccoli: la chat deve restare pulita. */}
      <div className="grid w-full gap-1.5 sm:gap-2">
        {esempi.map((esempio) => (
          <Card
            key={esempio}
            interactive
            onClick={() => onSend(esempio)}
            className="px-3 py-2 text-[12.5px] leading-snug text-ink-soft sm:px-4 sm:py-3 sm:text-[14px]"
          >
            «{esempio}»
          </Card>
        ))}
      </div>

      {store.account ? (
        <p className="text-[12px] text-ink-faint">
          Puoi scrivere <b className="font-bold text-ink-soft">@</b> per
          richiamare un immobile per nome.
        </p>
      ) : null}
    </div>
  );
}

// ── Composer ────────────────────────────────────────────────────────────────

function Composer({
  draft,
  setDraft,
  allegati,
  onAllega,
  onTogliAllegato,
  onSend,
  onStop,
  streaming,
  briefing,
  dictation,
  inputRef,
}: {
  draft: string;
  setDraft: (value: string) => void;
  allegati: AllegatoInCorso[];
  onAllega: (files: FileList | null) => void;
  onTogliAllegato: (chiave: string) => void;
  onSend: (text: string) => void;
  onStop: () => void;
  streaming: boolean;
  briefing: ChatBlock | null;
  dictation: ReturnType<typeof useDictation>;
  inputRef: React.RefObject<HTMLTextAreaElement | null>;
}) {
  const [listings, setListings] = useState<ListingView[]>([]);
  const [mention, setMention] = useState<MentionState | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const inCarico = allegati.some((a) => a.stato === "carico");
  const pronti = allegati.some((a) => a.stato === "pronto");
  const stretto = useSchermoStretto();
  const troppoLungo = draft.length > CARATTERI_MAX_MESSAGGIO;
  const puoInviare =
    (draft.trim() !== "" || pronti) && !inCarico && !troppoLungo;

  useEffect(() => {
    const element = inputRef.current;
    if (!element) return;
    element.style.height = "auto";
    // Cresce fino a quasi metà schermo: un testo incollato si deve poter
    // rileggere prima di mandarlo, non solo sei righe alla volta.
    const massimo = Math.max(168, Math.round(window.innerHeight * 0.45));
    element.style.height = `${Math.min(element.scrollHeight, massimo)}px`;
  }, [draft, inputRef]);

  useEffect(() => {
    void api
      .listings()
      .then(({ listings: loaded }) => setListings(loaded))
      .catch(() => setListings([]));
  }, []);

  const syncMention = () => {
    const element = inputRef.current;
    if (!element) return;
    setMention(
      findMention(
        element.value,
        element.selectionStart ?? element.value.length,
      ),
    );
  };

  const pickMention = (listing: ListingView) => {
    if (!mention) return;
    setDraft(applyMention(draft, mention, listing.nickname));
    setMention(null);
    requestAnimationFrame(() => {
      const element = inputRef.current;
      if (!element) return;
      const caret = mention.start + listing.nickname.length + 3;
      element.focus();
      element.setSelectionRange(caret, caret);
    });
  };

  return (
    <div className="border-t border-line px-6 pb-5 pt-3">
      <div className="mx-auto w-full max-w-[720px] space-y-2.5">
        <PendingBanner />
        {/* I chip del briefing («7 messaggi da rispondere», «3 check-in»)
            solo su schermo largo: su telefono rubano spazio alla chat, e le
            stesse cose stanno negli Avvisi e nel briefing stesso. */}
        {briefing ? (
          <div className="hidden sm:block">
            <BlockRenderer block={briefing} onSend={onSend} />
          </div>
        ) : null}
        {allegati.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {allegati.map((a) => (
              <FileChip
                key={a.chiave}
                nome={a.nome}
                tipo={a.tipo}
                stato={a.stato}
                {...(a.errore ? { errore: a.errore } : {})}
                onTogli={() => onTogliAllegato(a.chiave)}
              />
            ))}
          </div>
        ) : null}

        <div className="relative flex items-end gap-1.5 rounded-card-lg bg-surface-muted p-1.5 pl-2 transition-colors duration-quick focus-within:bg-surface-sunken">
          {mention ? (
            <MentionPopup
              listings={listings}
              query={mention.query}
              onPick={pickMention}
              onDismiss={() => setMention(null)}
            />
          ) : null}

          <IconButton
            label="Allega un PDF o una foto"
            onClick={() => fileRef.current?.click()}
            disabled={allegati.length >= ALLEGATI_PER_MESSAGGIO}
            className="size-9"
          >
            <Paperclip size={17} />
          </IconButton>
          <input
            ref={fileRef}
            type="file"
            accept={TIPI_ALLEGATO.join(",")}
            multiple
            hidden
            onChange={(event) => {
              onAllega(event.target.files);
              // Lo stesso file, riscelto dopo averlo tolto, deve ripartire.
              event.target.value = "";
            }}
          />
          {/* Su telefono lo spazio per scrivere è poco: la chiocciola si
              digita, e l'aiuto sta nel suggerimento sopra. */}
          <span className="hidden sm:block">
            <IconButton
              label="Richiama un immobile"
              onClick={() => setDraft(`${draft}@`)}
              className="size-9"
            >
              <AtSign size={17} />
            </IconButton>
          </span>

          <textarea
            ref={inputRef}
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
              setMention(
                findMention(
                  event.target.value,
                  event.target.selectionStart ?? 0,
                ),
              );
            }}
            onKeyUp={syncMention}
            onClick={syncMention}
            onBlur={() => setTimeout(() => setMention(null), 120)}
            onKeyDown={(event) => {
              if (
                mention &&
                ["Enter", "Tab", "ArrowUp", "ArrowDown", "Escape"].includes(
                  event.key,
                )
              )
                return;
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                if (puoInviare) onSend(draft);
              }
            }}
            rows={1}
            placeholder={stretto ? "Scrivi a Otto…" : "Scrivi un messaggio…"}
            aria-label="Messaggio"
            className="scroll-soft max-h-[45vh] min-h-[40px] flex-1 resize-none bg-transparent px-1.5 py-2.5 text-[14.5px] leading-relaxed text-ink outline-none placeholder:text-ink-faint"
          />

          {dictation.supported ? (
            <IconButton
              label={
                dictation.listening
                  ? "Ferma la dettatura"
                  : "Detta un messaggio"
              }
              onClick={dictation.toggle}
              className={cx(
                "size-9",
                dictation.listening &&
                  "bg-danger text-ink-inverse hover:bg-danger",
              )}
            >
              <Mic size={17} />
            </IconButton>
          ) : null}

          {streaming ? (
            <IconButton
              label="Ferma"
              tone="accent"
              onClick={onStop}
              className="bg-ink hover:bg-ink"
            >
              <Square size={13} fill="currentColor" />
            </IconButton>
          ) : (
            <IconButton
              label={inCarico ? "Sto caricando il file…" : "Invia"}
              tone={puoInviare ? "accent" : "sunken"}
              onClick={() => onSend(draft)}
              disabled={!puoInviare}
            >
              <ArrowUp size={18} strokeWidth={2.6} />
            </IconButton>
          )}
        </div>
        {draft.length > CARATTERI_MAX_MESSAGGIO * 0.9 ? (
          <p
            className={cx(
              "px-3 pb-2 text-[12px]",
              troppoLungo ? "text-danger" : "text-ink-faint",
            )}
          >
            {troppoLungo
              ? `Troppo lungo: togli ${(draft.length - CARATTERI_MAX_MESSAGGIO).toLocaleString("it-IT")} caratteri, o mandalo in due messaggi.`
              : `${draft.length.toLocaleString("it-IT")} caratteri su ${CARATTERI_MAX_MESSAGGIO.toLocaleString("it-IT")}.`}
          </p>
        ) : null}
      </div>
    </div>
  );
}

// ── Dettatura ───────────────────────────────────────────────────────────────

function useDictation(onText: (text: string) => void) {
  const [listening, setListening] = useState(false);
  const recognition = useRef<SpeechRecognitionLike | null>(null);

  const supported = useMemo(() => {
    if (typeof window === "undefined") return false;
    return Boolean(
      (window as unknown as { SpeechRecognition?: unknown })
        .SpeechRecognition ??
      (window as unknown as { webkitSpeechRecognition?: unknown })
        .webkitSpeechRecognition,
    );
  }, []);

  const toggle = useCallback(() => {
    if (!supported) return;

    if (listening) {
      recognition.current?.stop();
      setListening(false);
      return;
    }

    const Constructor = ((
      window as unknown as { SpeechRecognition?: SpeechRecognitionConstructor }
    ).SpeechRecognition ??
      (
        window as unknown as {
          webkitSpeechRecognition?: SpeechRecognitionConstructor;
        }
      ).webkitSpeechRecognition)!;

    const instance = new Constructor();
    instance.lang = "it-IT";
    instance.interimResults = false;
    instance.continuous = false;
    instance.onresult = (event) => {
      const text = event.results[0]?.[0]?.transcript;
      if (text) onText(text);
    };
    instance.onend = () => setListening(false);
    instance.onerror = () => setListening(false);

    recognition.current = instance;
    instance.start();
    setListening(true);
  }, [listening, onText, supported]);

  useEffect(() => () => recognition.current?.stop(), []);

  return { supported, listening, toggle };
}

interface SpeechRecognitionLike {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  start(): void;
  stop(): void;
  onresult:
    ((event: { results: Array<Array<{ transcript: string }>> }) => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
}

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

function upsertStep(steps: AgentStep[], step: AgentStep): AgentStep[] {
  const index = steps.findIndex((existing) => existing.id === step.id);
  if (index === -1) return [...steps, step];
  const next = [...steps];
  next[index] = step;
  return next;
}

// ── Allegati ────────────────────────────────────────────────────────────────

interface AllegatoInCorso {
  chiave: string;
  nome: string;
  tipo: string;
  stato: "carico" | "pronto" | "errore";
  id?: string;
  errore?: string;
}

/** Il motivo per cui un file non parte nemmeno, detto prima di caricarlo. */
function erroreAllegato(file: File): string | null {
  if (!(TIPI_ALLEGATO as readonly string[]).includes(file.type)) {
    return "Posso leggere PDF e foto (JPG, PNG, WebP).";
  }
  const massimo =
    file.type === "application/pdf" ? BYTE_MAX_ALLEGATO : BYTE_MAX_IMMAGINE;
  if (file.size > massimo) {
    return `Troppo grande: al massimo ${Math.round(massimo / 1024 / 1024)} MB.`;
  }
  return null;
}

function FileChip({
  nome,
  tipo,
  stato = "pronto",
  errore,
  onTogli,
}: {
  nome: string;
  tipo: string;
  stato?: AllegatoInCorso["stato"];
  errore?: string;
  onTogli?: () => void;
}) {
  const Icona = tipo === "application/pdf" ? FileText : ImageIcon;
  return (
    <div
      className={cx(
        "flex max-w-full items-center gap-2 rounded-field px-3 py-2 text-[13px] ring-1",
        stato === "errore"
          ? "bg-danger-soft text-danger ring-danger/25"
          : "bg-surface text-ink ring-line",
      )}
      title={errore ?? nome}
    >
      {stato === "carico" ? (
        <Loader2 size={15} className="shrink-0 animate-spin text-ink-faint" />
      ) : (
        <Icona size={15} className="shrink-0 text-ink-soft" />
      )}
      <span className="min-w-0 truncate font-semibold">{nome}</span>
      {errore ? <span className="shrink-0">· {errore}</span> : null}
      {onTogli ? (
        <button
          type="button"
          onClick={onTogli}
          aria-label={`Togli ${nome}`}
          className="ml-1 grid size-5 shrink-0 place-items-center rounded-full text-ink-faint hover:bg-surface-muted hover:text-ink"
        >
          <X size={13} />
        </button>
      ) : null}
    </div>
  );
}
